import React, { useMemo, Fragment } from 'react';
import styled from 'styled-components';

import LoadIcon from '../../../../components/LoadIcons';
import { useVerificationStatuses, useVerificationRefresh } from './context';

const Container = styled.div`
  border-top: 1px solid rgba(40, 56, 144, 0.1);
  margin: 0px 20px 28px;
  min-width: 402px;

  font-size: 10px;
  color: #677b8b;
  display: flex;
  justify-content: space-between;
  padding: 24px 0 16px;
  flex-direction: column;
  & > .label {
    font-weight: 700;
    display: flex;
    align-items: center;
    margin-bottom: 14px;
    justify-content: space-between;
    & > div {
      display: flex;
      align-items: center;
      gap: 12px;
    }
  }
  & > .date {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-bottom: 22px;

    & > div {
      display: flex;
      flex-direction: row;
      font-size: 14px;
    }

    & > .successful {
      & .status {
        color: white;
        background: #248a57;
      }

      & .name {
        font-weight: 700;
      }
    }
    & > .unsuccessful {
      & .status {
        color: #d8232a;
        background: #d8232a26;
      }
    }
    & > .tooltip {
      & .status {
        cursor: pointer;
        &::after {
          content: attr(data-tooltip);
          position: absolute;
          bottom: 15px;
          left: 15px;
          transform: translateX();

          background: black;
          color: white;
          padding: 5px 8px;
          border-radius: 4px;
          font-size: 12px;

          opacity: 0;
          pointer-events: none;
          transition: opacity 0.2s;
        }

        &:hover::after {
          opacity: 1;
        }
      }
      &.GEOPOSITION {
        & .status::after {
          width: 200px;
        }
      }
      &.VISUAL_MATCH {
        & .status::after {
          width: var(--tooltip-width, 200px);
          white-space: pre-line;
        }
      }
      &.DATETIME,
      &.CAPTURE_CODE,
      &.ORIGINAL_PHOTO {
        & .status::after {
          text-wrap: nowrap;
        }
      }
    }

    & .status {
      width: 22px;
      min-width: 22px;
      height: 22px;
      display: flex;
      justify-content: center;
      align-items: center;
      border-radius: 50%;
      margin-right: 10px;
      font-size: 10px;
      position: relative;
    }

    & .decimal {
      min-width: 10px;
    }

    & .message {
      max-width: 250px;
    }
  }
`;

const measureMultilineWidth = (() => {
  let ctx = null;
  return (text, font = '12px NunitoSans, sans-serif') => {
    if (!ctx) {
      const canvas = document.createElement('canvas');
      ctx = canvas.getContext('2d');
    }
    ctx.font = font;
    const lines = text.split('\n');
    return Math.ceil(Math.max(...lines.map((l) => ctx.measureText(l).width)));
  };
})();

export default (props) => {
  const statuses = useVerificationStatuses();
  const onRefres = useVerificationRefresh();

  const providers = useMemo(() => {
    return Object.keys(statuses || {}).filter((provider) =>
      Boolean(statuses?.[provider]?.list?.length)
    );
  }, [statuses]);

  const tooltipWidths = useMemo(() => {
    const map = {};
    if (!statuses) return map;
    Object.keys(statuses).forEach((provider) => {
      (statuses[provider]?.list || []).forEach((s) => {
        if (s.module === 'VISUAL_MATCH' && s.tooltip) {
          const px = measureMultilineWidth(s.tooltip);
          map[`${provider}__${s.module}`] = px + 20; // +20px padding
        }
      });
    });
    return map;
  }, [statuses]);

  const handleRefresh = (provider) => {
    onRefres(provider);
  };


  return (
    <Container {...props}>
      {providers.map((provider) => (
        <Fragment key={`status--${provider}`}>
          <div className='label'>
            <div>{statuses[provider].title}</div>
            <div>
              {statuses[provider].date}
              {!statuses[provider].progress && (
                <LoadIcon type='btn' onClick={() => handleRefresh(provider)} />
              )}
            </div>
          </div>
          <div className='date'>
            {statuses[provider].progress ? (
              <>Verification in progress... Results will appear shortly</>
            ) : (
              statuses[provider].list.map((s) => (
                <div className={s.className} key={`status--${provider}--${s.module}`}>
                  <div
                    className='status'
                    data-tooltip={s.tooltip || ''}
                    style={
                      tooltipWidths[`${provider}__${s.module}`]
                        ? { '--tooltip-width': `${tooltipWidths[`${provider}__${s.module}`]}px` }
                        : undefined
                    }
                  >
                    {s.status ? '✔' : '✖'}
                  </div>
                  <div className='name'>
                    {s.modeleName}
                    {!s.status && <>: {s.failReason}</>}
                    {Boolean(s.confidence) && <> ({s.confidenceText})</>}
                  </div>
                </div>
              ))
            )}
          </div>
        </Fragment>
      ))}
    </Container>
  );
};
